import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Searchable single-select combobox. Free-text filter that matches on option label
 * (accent-insensitive, case-insensitive, substring match — you can type "smurfit"
 * in the middle of the string and it will be found).
 *
 * Props:
 *  - value: string | ""  (controlled)
 *  - onChange: (newValue: string) => void
 *  - options: [{ value: string, label: string }]
 *  - placeholder: string (button placeholder when no value)
 *  - searchPlaceholder: string (input placeholder inside dropdown)
 *  - emptyText: string (shown when no options match)
 *  - disabled: boolean
 *  - allowClear: boolean (default true) — show × to clear the selection
 *  - "data-testid": string
 */
export function Combobox({
  value,
  onChange,
  options = [],
  placeholder = "Seleziona…",
  searchPlaceholder = "Cerca…",
  emptyText = "Nessun risultato",
  disabled = false,
  allowClear = true,
  className,
  ...rest
}) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value);

  const normalize = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-slate-500",
            className
          )}
          data-testid={rest["data-testid"]}
        >
          <span className="truncate text-left">
            {selected ? selected.label : placeholder}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {allowClear && selected && !disabled && (
              <span
                role="button"
                aria-label="Cancella selezione"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange("");
                }}
                className="p-0.5 rounded hover:bg-slate-200 opacity-70 hover:opacity-100"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            <ChevronsUpDown className="w-4 h-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue is the CommandItem `value` prop → we use option label there
            const n = normalize(itemValue);
            const q = normalize(search);
            return n.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => {
                    onChange(o.value === value ? "" : o.value);
                    setOpen(false);
                  }}
                  data-testid={`${rest["data-testid"] || "combobox"}-opt-${o.value}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      o.value === value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="truncate">{o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
